import { fromJS } from 'immutable';
import appConfig from 'app/config/client';
import { CLIENT_OUTDATED, REQUIRE_INTERACTIVE_RELOAD } from 'app/remote/client/constants';
import { getIsBehindBrokenProxy } from 'app/remote/client/selectors';
import { info } from 'app/services/logger';
import { notify } from 'app/services/errorReporter';
import { getCacheCheck, getClientVersion } from 'app/utils/api';
import {
  getClientVersionField,
  getBreakingChangeTimestamp,
  getLocalClientVersion,
  getLocalBreakingChangeTimestamp,
  requiresClientVersionCheck,
} from 'app/utils/clientVersion';
import FluentdLogger from 'app/services/fluentdLogger';
import { getGlobalHistory } from 'app/services/history';
import { request } from 'app/models/request/actions';
import { setItem } from 'app/models/sessionStorage/actions';
import { BEHIND_BROKEN_PROXY_ID } from 'app/models/sessionStorage/constants';

const clientConfig = appConfig.client;

export const clientOutdated = () => ({ type: CLIENT_OUTDATED });
export const requireInteractiveReload = () => ({ type: REQUIRE_INTERACTIVE_RELOAD });

/**
 * Checks to see if the client version has changed. This action returns a promise containing
 * an object with booleans `outdated` and `refreshRequired`. `outdated` is true if the latest
 * version is different. `refreshRequired` is true if there has been a new breaking change
 * since our clients last known breaking change.
 */
export const isClientOutdated = (
  // these parameters are here to facilitate testing
  currentVersion = getLocalClientVersion(),
  currentBreakingChangeTimestamp = getLocalBreakingChangeTimestamp(),
) => (dispatch) =>
  dispatch(request(getClientVersion()))
    .then(response => {
      const version = getClientVersionField(response);
      const breakingChangeTimestamp = getBreakingChangeTimestamp(response);

      const hasBreakingChange = currentBreakingChangeTimestamp < breakingChangeTimestamp;

      // generally, if there's a breaking change, then there should have been a
      // version change but we're just making sure here
      const outdated = hasBreakingChange || currentVersion !== version;

      if (outdated) {
        info('Client is outdated', {
          currentVersion,
          currentBreakingChangeTimestamp,
          version,
          breakingChangeTimestamp,
        });
      }

      return { outdated, hasBreakingChange };
    })
    .catch(reason => {
      notify(reason, { name: 'Client.checkClientVersion' });
      return Promise.reject(reason);
    });

/**
 * Wraps an error handler with client version error checking.
 *
 * @param {Function} errorHandler       method to call to pass error to diagnostic tools
 */
export const makeErrorHandlerWithClientChecking = (
  errorHandler,
  // this parameter is here to facilitate testing
  isClientOutdatedCallback = isClientOutdated,
) => (error, ...rest) => dispatch => {
  const reportError = () => errorHandler(error, ...rest);

  // See if the error is the kind of error that should merit a client version check.
  if (requiresClientVersionCheck(error)) {
    // See if the client is outdated or if they are behind a broken proxy.
    Promise.all([
      dispatch(checkBrokenProxyStatus()),
      dispatch(isClientOutdatedCallback()),
    ])
      .then(([isBehindProxy, { outdated }]) => {
        // If the client is out of date, ignore the error and dispatch an event indicating the
        // client needs to be updated. Also, if the user is behind a bad proxy, ignore the error
        // since we are adding cache busting and the API is still returning bad data.
        if (outdated) {
          dispatch(requireInteractiveReload());
        } else if (!isBehindProxy) {
          reportError();
        }
      });
  } else {
    reportError();
  }
};

/**
 * Sets up a listener that will periodically check if the client version is outdated.
 *
 * @param {Number} checkVersionTime     in milliseconds, time to check the client is outdated
 * @param {Number} clientExpiryTimeout  how long to wait before forcing a refresh
 * @returns {() => void}                thunk that can be dispatched to start listening
 */
export const clientVersionListener = (
  checkVersionTime = clientConfig.checkVersionInterval,
  clientExpiryTimeout = clientConfig.clientExpiryTimeout,
  // this parameter is here to facilitate testing
  isClientOutdatedCallback = isClientOutdated,
) => (dispatch) => {
  // eslint-disable-next-line prefer-const
  let checkVersionId;
  // eslint-disable-next-line prefer-const
  let clientExpiryId;

  // Stops timer for forcing refresh and checking the client version.
  const stopTimers = () => {
    clearInterval(checkVersionId);
    clearTimeout(clientExpiryId);
  };

  const reloadOnNavigate = () => {
    getGlobalHistory().setWantsReloadOnNavigate(true);
  };

  const outdatedHandler = ({ outdated, hasBreakingChange }) => {
    if (outdated) {
      dispatch(clientOutdated());
      // make the next page navigation a refresh instead of just handling it in javascript
      reloadOnNavigate();

      if (hasBreakingChange) {
        // display an obnoxious error telling the user to refresh the page
        dispatch(requireInteractiveReload());
        stopTimers();
      }
    }
  };

  // Callback for setInterval.
  const callback = () => dispatch(isClientOutdatedCallback()).then(outdatedHandler);

  checkVersionId = setInterval(callback, checkVersionTime);
  clientExpiryId = setTimeout(reloadOnNavigate, clientExpiryTimeout);

  // Start by checking to see if the client version is outdated.
  callback();

  return stopTimers;
};

// Used to cache the results of checkBrokenProxyStatus.
const checkBrokenProxyStatusCache = new WeakMap();

/**
 * This method calls a special API that should return the
 * @returns {() => () => Promise<unknown>}
 */
export const checkBrokenProxyStatus = () => (dispatch, getState) => {
  /**
   * @returns {Promise<Boolean>}
   */
  const doFetch = () => {
    // If the user is already behind a proxy, simply resolve.
    if (getIsBehindBrokenProxy(getState())) {
      // Log the message here, in case it fell through the cracks when reloading the page.
      FluentdLogger.info({
        name: 'Request.checkBrokenProxyStatus',
        message: 'Broken proxy already detected.',
      });
      return Promise.resolve(true);
    } else {
      // NOTE: Both requests should happen sequentially. Some browsers will notice that a request
      // is pending and cache the results. Even if Cache-Control headers are set to "no-cache"
      // or "private, max-age=0".
      return dispatch(request(getCacheCheck()))

      // Only extract the response from before so it can be passed down to the next request.
      .then((response) =>
        Promise.all([
          dispatch(request(getCacheCheck())),
          response,
        ]),
      )

      // Take both responses and compare equality to determine whether or not to persist the
      // cache buster.
      .then(responses => {
        const responseA = fromJS(responses[0]);
        const responseB = fromJS(responses[1]);

        // If the responses match, then the user is behind a proxy that is caching requests.
        // Log the results, set a flag in the session and refresh the page so all the APIs can be
        // called with the cache busting flag.
        if (responseA.equals(responseB)) {
          // Try to log the message, but don't force the user to wait for it to send before
          // refreshing the page.
          FluentdLogger.info({
            name: 'Request.checkBrokenProxyStatus',
            message: 'Broken proxy detected.',
          });
          dispatch(setBehindProxy(true));
          getGlobalHistory().reload();
        }
      })

      // TODO: Are there any assumptions we can make about the proxy if there is an error?
      // Perhaps if the error is a 502 (Bad Gateway) we should consider setting the flag to
      // true?
      .catch(reason => {
        notify(reason, { name: 'Request.checkBrokenProxyStatus' });
      })

      // Return the result of checking the cache.
      .then(() =>
        getIsBehindBrokenProxy(getState()),
      );
    }
  };

  // If there is no check in the cache, create the promise for the fetch and store it for
  // later. Use dispatch, which is unique to the instance of the store.
  if (!checkBrokenProxyStatusCache.has(dispatch)) {
    checkBrokenProxyStatusCache.set(dispatch, doFetch());
  }

  return checkBrokenProxyStatusCache.get(dispatch);
};

export const setBehindProxy = (result) => (dispatch, getState) => {
  dispatch(setItem(BEHIND_BROKEN_PROXY_ID, result));
};
